import { normalizeDateOnly } from 'src/utils/date';
import api from '../../shared/api/axios';
import type { WbsItemVersion, WbsList } from './types';
// import type { ViewAs } from './components/overview/Overview';

type AsOfQuery = { viewAs: 'DATE' | 'WEEK'; at: string };

// 이 유틸을 사용하면 데이터베이스에 저장될 때 하루가 뒤로 밀린다. 날짜뒤에 T00:00.000Z 처럼 로컬 시간 기반 UTC 변환 결과로 보내야 한다.
// const toISO = (v?: string | null) => (v ? new Date(v).toISOString() : null);

/** 서버 계약에 맞춰 날짜/가중치/퍼센트 정규화 */
export function normalizePatch(patch: Partial<WbsItemVersion>) {
    const x: any = { ...patch };
    if (x.plannedStart !== undefined) x.plannedStart = normalizeDateOnly(x.plannedStart);
    if (x.plannedFinish !== undefined) x.plannedFinish = normalizeDateOnly(x.plannedFinish);
    // weight는 0 ~ 1 -> 0 ~ 100% 로 정책 변경
    // if (x.weight != null && x.weight > 1) x.weight = x.weight / 100; // % → 0~1
    if (x.plannedProgress != null && x.plannedProgress <= 1) x.plannedProgress *= 100; // 0~1 → %
    return x;
}

/* --------------------------
   읽기 API
--------------------------- */

/** 현재 활성 리스트 + 아이템 */
export async function getCurrentList(projectId: string, q?: AsOfQuery) {
    const { data } = await api.get(`/projects/${projectId}/wbs/lists/current`, { params: q });
    return (data?.data ?? data) as { list: any; items: any[] } | null;
}

// TODO: 위 함수 이름이 불명확하여 점진적으로 이것으로 교체할 것.
// export async function getCurrentPublished(projectId: string) {
//     const { data } = await api.get(`/projects/${projectId}/wbs/lists/current`);
//     return (data?.data ?? data) as { list: any; items: any[] } | null;
// }

/** 모든 버전 메타 목록 */
export async function listAllVersions(projectId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/lists`);
    return (data?.data ?? data) as WbsList[];
}

/** (동일) 버전 메타 목록 */
export async function listVersions(projectId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/lists`);
    return data?.data ?? data; // [{ version, isCurrent, createdAt, activatedAt, ... }]
}

/** 특정 버전 스냅샷 */
export async function getListByVersion(projectId: string, version: number, q?: AsOfQuery) {
    const { data } = await api.get(`/projects/${projectId}/wbs/lists/${version}`, { params: q });
    return data?.data ?? data;
}

/** 버전 활성화 */
export async function activateVersion(projectId: string, version: number) {
    const { data } = await api.post(`/projects/${projectId}/wbs/lists/${version}/activate`);
    return data?.data ?? data;
}

/** 미발행 변경(드래프트) 요약 */
export async function listUnpublishedChanges(projectId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/changes`);
    return data?.data ?? data;
}

/* --------------------------
   드래프트 저장/수정/이동/삭제
--------------------------- */

export type SaveDraftInput = {
    nodeId?: string | null; // 없으면 새 노드 + 드래프트 생성
    parentNodeId?: string | null;
    code?: string;
    name: string;
    plannedStart?: string | null; // YYYY-MM-DD
    plannedFinish?: string | null; // YYYY-MM-DD
    weight?: number | null; // 0 ~ 100%
    level?: number | null;
    memo?: string | null;
    isDeleted?: boolean | null; // 소프트 삭제 표식(선택)
    orderIndex?: number | null;
};

/** @deprecated 구 draft 엔드포인트 사용. WbsEditor bootstrap + batch(update-only) 정책으로 통일됨 */
/** 단건 드래프트 저장(신규/수정 겸용) — 기존 함수 유지 */
export async function saveDraftItem(projectId: string, input: SaveDraftInput) {
    throw new Error('DEPRECATED: saveDraftItem() uses legacy /wbs/items/*/draft endpoints.');
    // eslint-disable-next-line no-unreachable
    const url = input.nodeId
        ? `/projects/${projectId}/wbs/items/${input.nodeId}/draft`
        : `/projects/${projectId}/wbs/items/draft`;

    // 날짜를 ISO(자정)로 보내도록 기존 규약 유지
    const body = {
        ...input,
        plannedStart: input.plannedStart ?? null,
        plannedFinish: input.plannedFinish ?? null,
    };
    const { data } = await api.post(url, body);
    return data?.data ?? data;
}

/** @deprecated 구 draft 엔드포인트 사용. WbsEditor bootstrap + batch(update-only) 정책으로 통일됨 */
/** 드래프트 저장(부분 변경) — 기존 함수 유지 */
export async function saveDraft(projectId: string, nodeId: string, patch: Partial<WbsItemVersion>) {
    throw new Error('DEPRECATED: saveDraft() uses legacy /wbs/items/:nodeId/draft endpoint.');
    // eslint-disable-next-line no-unreachable
    const body = normalizePatch(patch);
    const { data } = await api.post(`/projects/${projectId}/wbs/items/${nodeId}/draft`, body);
    return data?.data ?? data;
}

/** @deprecated 구 draft 엔드포인트 사용. WbsEditor bootstrap + batch(update-only) 정책으로 통일됨 */
/** 이동/들여쓰기/내어쓰기/순서 변경 헬퍼
 *  - 전용 move 라우트가 없으므로 saveDraft로 parent/level 등을 갱신
 *  - orderIndex는 서버가 관리하지 않는다면 code/level 조합으로 처리하거나,
 *    서버 로직과 약속된 필드를 추가하세요(예: position, orderIndex 등).
 */
export async function moveDraftItem(
    projectId: string,
    nodeId: string,
    opts: {
        parentNodeId: string | null;
        level?: number | null;
        // 필요 시 orderIndex?: number;
        // 필요 시 code?: string;  // 표시용 코드가 서버에 필요할 때
    },
) {
    throw new Error('DEPRECATED: moveDraftItem() uses legacy draft save endpoint.');
    // eslint-disable-next-line no-unreachable
    const { parentNodeId, level = null } = opts;
    return saveDraft(projectId, nodeId, { parentNodeId, level } as any);
}

/** @deprecated 구 draft 엔드포인트 사용. WbsEditor bootstrap + discard drafts로 통일됨 */
/** 드래프트 삭제(폐기) — 실제 노드 삭제 아님 */
export async function deleteDraft(projectId: string, nodeId: string) {
    throw new Error('DEPRECATED: deleteDraft() uses legacy /wbs/items/:nodeId/draft endpoint.');
    // eslint-disable-next-line no-unreachable
    const { data } = await api.delete(`/projects/${projectId}/wbs/items/${nodeId}/draft`);
    return data?.data ?? data;
}

/** 노드 삭제 요청(소프트): publish 시 반영됨 */
export async function requestDeleteNode(projectId: string, nodeId: string, mode: 'single' | 'subtree' = 'subtree') {
    const { data } = await api.post(`/projects/${projectId}/wbs/items/${nodeId}/delete-request`, null, {
        params: { mode },
    });
    return data?.data ?? data;
}

/* --------------------------
   발행(새 버전 생성)
--------------------------- */

/** 발행 — 기존 publishList를 표준으로 사용 */
export async function publishList(
    projectId: string,
    draftVersionIds: string[],
    opts?: { title?: string; note?: string; activateNow?: boolean },
) {
    const { data } = await api.post(`/projects/${projectId}/wbs/lists/publish`, {
        draftVersionIds,
        ...(opts ?? {}),
    });
    return data?.data ?? data; // { version, ... }
}

/** (선택) 선택 발행 — 기존 이름 유지하되 내부적으로 publishList 호출 */
export async function publishNewListVersion(
    projectId: string,
    args: { title?: string; note?: string; selectedNodeIds?: string[] },
) {
    // 백엔드가 selectedNodeIds를 직접 받도록 구현돼 있다면 그대로 전달
    const { data } = await api.post(`/projects/${projectId}/wbs/lists/publish`, args);
    return data?.data ?? data; // { version, ... }
}

/* --------------------------
   EV/진척 관련 (기존 유지)
--------------------------- */
export async function submitProgress(
    projectId: string,
    nodeId: string,
    weekOf: string,
    ev: number,
    listVersionUsed?: number,
) {
    const body: any = {
        nodeId,
        // wbsId: nodeId,
        weekOf: weekOf, // YYYY-MM-DD
        // weekISO: weekOfISO.slice(0, 10),
        earnedValue: ev,
        // earnedValuePct: ev,
    };
    if (typeof listVersionUsed === 'number') body.listVersionUsed = listVersionUsed;
    try {
        const { data } = await api.post(`/projects/${projectId}/wbs/progress/submissions`, body);
        return data?.data ?? data;
    } catch (e: any) {
        console.error('submitProgress 400 detail: ', e?.response?.data || e);
        throw e;
    }
}

export async function approveSubmission(projectId: string, submissionId: string) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/${submissionId}/approve`, {});
    return data?.data ?? data;
}

export async function rejectSubmission(
    projectId: string,
    submissionId: string,
    payload: { reason?: string; expectedEarnedValue: number },
) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/${submissionId}/reject`, payload);
    return data?.data ?? data;
}

/* --------------------------
   주간 플래그/히스토리 (기존 유지)
--------------------------- */
// export async function listFlags(projectId: string, weekOf: string, nodeIds: string[]) {
//     const q = new URLSearchParams({ weekOf: weekOf, ids: nodeIds.join(',') });
//     const { data } = await api.get(`/projects/${projectId}/wbs/progress/flags?${q.toString()}`);
//     return data?.data ?? data;
// }
export async function listFlags(projectId: string, weekOf: string, nodeIds: string[]) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/flags`, {
        weekOf,
        nodeIds,
    });
    return data?.data ?? data;
}

export async function listProgressHistory(projectId: string, nodeId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/progress/history`, { params: { wbsId: nodeId } });
    return data?.data ?? data;
}

export async function apiGetSubmissionLogs(projectId: string, submissionId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/progress/submissions/${submissionId}/logs`);
    return data?.data ?? data;
}

export async function apiGetApprovedMap(projectId: string, weekOf: string, nodeIds: string[]) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/approved-map`, { weekOf: weekOf, nodeIds });
    return data?.data ?? data;
}

export async function apiGetWeeklyProgressComparison(projectId: string, atISO: string, nodeIds: string[]) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/weekly-comparison`, {
        at: atISO,
        nodeIds,
    });
    return data?.data ?? data;
}

export async function apiBackfillProgressHistory(
    projectId: string,
    body: {
        at?: string; // optional
        targetEvToPvRatio: number;
        // weeksBack: number;
        pattern: 'UNIFORM' | 'FRONT_DELAY' | 'MID_DIP';
        // resetExisting은 서버에서 강제 reset한다면 굳이 안보내도 됨
        resetExisting?: boolean;
        listVersionUsed?: number;
    },
) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/backfill-history`, body);
    return data?.data ?? data;
}

export async function apiResetBackfillHistory(
    projectId: string,
) {
    const { data } = await api.post(`/projects/${projectId}/wbs/progress/reset-backfill`);
    return data?.data ?? data;
}
